import React, { useState, useEffect } from 'react';
import { useUser } from '@/context/UserContext';
import UserProfileModal from './UserProfileModal';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Menu,
  User,
  MessageSquare,
  Home,
  Search,
  Globe,
  Database,
  Bell,
  BookOpen,
  Lock,
  LogOut,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ModeToggle } from '@/components/ui/mode-toggle';
import { NotificationBell } from './NotificationCenter';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import BrandMark from '@/components/BrandMark';
import { apiService, DASHBOARD_AUTH_CHANGED_EVENT, getDashboardToken } from '@/api/apiService';
import { toast } from 'react-hot-toast';

interface NavbarProps {
  toggleSidebar: () => void;
  showSidebar?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar, showSidebar = true }) => {
  const { userProfile } = useUser();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [hasDashboardSession, setHasDashboardSession] = useState(Boolean(getDashboardToken()));
  const [dashboardEmail, setDashboardEmail] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const handleDashboardLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await apiService.dashboardLogout();
      toast.success('Signed out');
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      const detail =
        error && typeof error === 'object' && 'response' in error
          ? String(
              (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || ''
            )
          : '';
      toast.error(detail || 'Failed to sign out');
    } finally {
      setLoggingOut(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const refreshRole = async () => {
      const token = getDashboardToken();
      if (!token) {
        if (!cancelled) {
          setIsPlatformAdmin(false);
          setHasDashboardSession(false);
          setDashboardEmail('');
        }
        return;
      }
      try {
        const payload = await apiService.dashboardMe();
        if (!cancelled) {
          setHasDashboardSession(true);
          setIsPlatformAdmin(Boolean(payload?.user?.is_platform_admin));
          setDashboardEmail(String(payload?.user?.email || ''));
        }
      } catch {
        if (!cancelled) {
          setIsPlatformAdmin(false);
          setHasDashboardSession(false);
          setDashboardEmail('');
        }
      }
    };

    void refreshRole();
    const onAuthChanged = () => {
      void refreshRole();
    };
    window.addEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(DASHBOARD_AUTH_CHANGED_EVENT, onAuthChanged as EventListener);
    };
  }, [pathname]);

  const navItems = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Chat', href: '/chat', icon: MessageSquare },
    { name: 'Crawl', href: '/crawl', icon: Globe },
    { name: 'Search', href: '/search', icon: Search },
    { name: 'Sites', href: '/sites', icon: Database },
  ];

  const showProfileMenu = hasDashboardSession || isPlatformAdmin;

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-border bg-background px-6">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className={cn('hover:bg-accent', (isDesktop || !showSidebar) && 'hidden')}
        aria-label="Toggle sidebar"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex items-center">
        <Link
          to={hasDashboardSession ? (isPlatformAdmin ? '/' : '/dashboard') : '/dashboard'}
          className="text-foreground hover:text-foreground"
        >
          <BrandMark />
        </Link>
      </div>

      {!isDesktop && showSidebar && (
        <div className="hidden md:flex items-center ml-6 space-x-6 text-sm font-medium">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'transition-colors hover:text-foreground/80 flex items-center gap-2',
                pathname === item.href ? 'text-foreground' : 'text-foreground/60'
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </Link>
          ))}
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        {isPlatformAdmin && <NotificationBell />}
        <ModeToggle />

        {showProfileMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-accent">
                <Avatar className="h-9 w-9">
                  {userProfile.avatar ? (
                    <AvatarImage src={userProfile.avatar} alt={userProfile.name} />
                  ) : (
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {(dashboardEmail || userProfile.name || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-max min-w-56 max-w-[calc(100vw-2rem)] bg-popover border-border"
              align="end"
              forceMount
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-1.5">
                  {hasDashboardSession && dashboardEmail ? (
                    <>
                      <p className="px-1.5 py-0.5 text-sm font-medium leading-normal">
                        {dashboardEmail}
                      </p>
                      <p className="px-1.5 text-xs leading-normal text-muted-foreground">
                        Dashboard account
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="px-1.5 py-0.5 text-sm font-medium leading-normal">
                        {userProfile.name}
                      </p>
                      <p className="px-1.5 text-xs leading-normal text-muted-foreground">
                        User Profile
                      </p>
                    </>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              {isPlatformAdmin ? (
                <>
                  <DropdownMenuItem
                    onClick={() => setIsProfileModalOpen(true)}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent focus:bg-accent">
                    <Link to="/notifications">
                      <Bell className="mr-2 h-4 w-4" />
                      <span>Notification Info</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent focus:bg-accent">
                    <Link to="/preferences">
                      <User className="mr-2 h-4 w-4" />
                      <span>User Preferences</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent focus:bg-accent">
                    <Link to="/guide">
                      <BookOpen className="mr-2 h-4 w-4" />
                      <span>User Guide</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border" />
                </>
              ) : null}
              {hasDashboardSession ? (
                <>
                  <DropdownMenuItem asChild className="hover:bg-accent focus:bg-accent">
                    <Link to="/dashboard/security">
                      <Lock className="mr-2 h-4 w-4" />
                      <span>Security</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive hover:bg-accent focus:bg-accent"
                    disabled={loggingOut}
                    onSelect={(event) => {
                      event.preventDefault();
                      void handleDashboardLogout();
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{loggingOut ? 'Signing out…' : 'Sign out'}</span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <UserProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} />
    </header>
  );
};

export default Navbar;
